import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiV2Fetch } from './api';
import {
  CONTROL_ONBOARDING_ENDPOINT,
  CONTROL_STATUS_ENDPOINT,
  PREFERENCES_ENDPOINT,
} from './api-contract';
import type { UpdatePreferencesPayload, UserPreferences } from '@/types';

export const queryKeys = {
  controlStatus: ['control', 'status'] as const,
  controlOnboarding: ['control', 'onboarding'] as const,
};

export const preferencesQueryKey = ['preferences'] as const;

export function usePreferences() {
  return useQuery<UserPreferences, ApiError>({
    queryKey: preferencesQueryKey,
    queryFn: () => apiV2Fetch<UserPreferences>(PREFERENCES_ENDPOINT),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation<UserPreferences, ApiError, UpdatePreferencesPayload>({
    mutationFn: (payload) =>
      apiV2Fetch<UserPreferences>(PREFERENCES_ENDPOINT, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (updatedPrefs) => {
      queryClient.setQueryData(preferencesQueryKey, updatedPrefs);
    },
  });
}

type ControlStatusResponse = {
  orgId: string;
  secrets: Record<string, { hasValue: boolean }>;
  control: {
    outboxOk: boolean;
    auditOk: boolean;
    webhooksConfigured: boolean;
  };
};

/**
 * Mirrors GET/PATCH /control/onboarding (backend routes/control.ts): per-step
 * booleans only — the backend returns no completion timestamp, so "complete"
 * is derived client-side from the step flags.
 */
export type OnboardingState = {
  orgId: string;
  clinic: {
    profileDone: boolean;
    servicesDone: boolean;
    availabilityDone: boolean;
  };
  staffing: {
    icpDone: boolean;
    firstRunDone: boolean;
  };
};

export function useControlStatus() {
  return useQuery<ControlStatusResponse, ApiError>({
    queryKey: queryKeys.controlStatus,
    queryFn: () => apiV2Fetch<ControlStatusResponse>(CONTROL_STATUS_ENDPOINT),
    staleTime: 30000,
  });
}

export function useOnboardingState() {
  return useQuery<OnboardingState, ApiError>({
    queryKey: queryKeys.controlOnboarding,
    queryFn: () => apiV2Fetch<OnboardingState>(CONTROL_ONBOARDING_ENDPOINT),
    staleTime: 10000,
  });
}

export function useUpdateOnboardingState() {
  const queryClient = useQueryClient();

  return useMutation<
    OnboardingState,
    ApiError,
    Partial<{
      clinic: Partial<OnboardingState['clinic']>;
      staffing: Partial<OnboardingState['staffing']>;
    }>
  >({
    mutationFn: (updates) =>
      apiV2Fetch<OnboardingState>(CONTROL_ONBOARDING_ENDPOINT, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onSuccess: (updatedState) => {
      queryClient.setQueryData(queryKeys.controlOnboarding, updatedState);
    },
  });
}

export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

export function isRetryableError(error: unknown): boolean {
  return error instanceof ApiError && error.retryable;
}

/** Friendly copy for backend error codes, used when the response carried no message. */
const CODE_MESSAGES: Record<string, string> = {
  AI_KEY_MISSING: 'Run failed - check your API keys in Settings',
  INGEST_KEY_MISSING: 'Data provider key not configured - check Settings',
  INGEST_FAILED: 'Data provider request failed',
  AI_PARSE_FAILED: 'AI response could not be processed',
  RUN_FAILED: 'Run failed - please try again or contact support',
  EXPORT_NOT_READY: 'Run has no export artifacts yet',
  INTERNAL: 'Something went wrong',
};

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // The backend error string is already sanitized — prefer it verbatim.
    if (error.message) return error.message;
    if (error.code && CODE_MESSAGES[error.code]) return CODE_MESSAGES[error.code]!;
    return 'Something went wrong';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
